import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Sparkle, GoogleLogo } from '@phosphor-icons/react';

export default function Signup() {
  const [sp] = useSearchParams();
  const [form, setForm] = useState({ full_name: '', email: '', password: '', referral_code: sp.get('ref') || '' });
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form);
      toast.success('Welcome to 4xview!');
      nav('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Signup failed');
    } finally { setLoading(false); }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 relative overflow-hidden">
      <div className="hero-glow" style={{ top: -200, right: -200 }} />
      <div className="w-full max-w-md relative z-10">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <div className="w-10 h-10 rounded-lg gradient-orange flex items-center justify-center shadow-glow-saffron"><Sparkle size={22} weight="fill" /></div>
          <span className="font-heading font-bold text-2xl">4xview</span>
        </Link>
        <div className="glass-strong p-8 rounded-2xl neon-border">
          <h1 className="font-heading text-3xl font-bold mb-2">Create account</h1>
          <p className="text-muted-foreground text-sm mb-6">Start with a free account — upgrade anytime</p>
          <Button onClick={google} variant="outline" data-testid="google-signup-btn" className="w-full mb-4 h-11 bg-white/5 hover:bg-white/10 border-white/10 text-white">
            <GoogleLogo size={18} weight="bold" className="mr-2" /> Sign up with Google
          </Button>
          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-white/10" /><span className="text-xs text-muted-foreground uppercase tracking-widest">or</span><div className="flex-1 h-px bg-white/10" />
          </div>
          <form onSubmit={submit} className="space-y-4">
            <div><Label className="data-label">Full Name</Label>
              <Input data-testid="signup-name" required value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})} className="mt-1 bg-surface-base border-white/10 focus:border-saffron h-11" /></div>
            <div><Label className="data-label">Email</Label>
              <Input data-testid="signup-email" type="email" required value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="mt-1 bg-surface-base border-white/10 focus:border-saffron h-11" /></div>
            <div><Label className="data-label">Password</Label>
              <Input data-testid="signup-password" type="password" required minLength={6} value={form.password} onChange={e => setForm({...form, password: e.target.value})} className="mt-1 bg-surface-base border-white/10 focus:border-saffron h-11" /></div>
            <div><Label className="data-label">Referral (optional)</Label>
              <Input data-testid="signup-referral" value={form.referral_code} onChange={e => setForm({...form, referral_code: e.target.value})} className="mt-1 bg-surface-base border-white/10 focus:border-saffron h-11" /></div>
            <Button type="submit" disabled={loading} data-testid="signup-submit" className="w-full h-11 bg-saffron hover:bg-saffron-hover text-white font-semibold shadow-glow-saffron">
              {loading ? 'Creating…' : 'Create account'}
            </Button>
          </form>
          <p className="text-sm text-center text-muted-foreground mt-6">
            Have an account? <Link to="/login" className="text-saffron hover:underline font-semibold">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
